/* eslint-disable prefer-const */
/* eslint-disable no-underscore-dangle */
/* eslint-disable no-useless-escape */
import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { jwtDecode } from 'jwt-decode';

import { JWTTokenDTO } from '../redux/identity/identityTypes';
import {
 clearAuthTokens,
 getAuthCookies,
 persistAuthTokens,
} from '../utils/authTokens';

const axiosConfig = {
 baseURL: import.meta.env.VITE_API_URL,
 timeout: 10000,
 headers: {
  'Content-Type': 'application/json',
 },
};

export const axiosInstance = axios.create(axiosConfig);

export const get = (url: string) => axiosInstance.get(url);
export const post = (url: string, data: any) => axiosInstance.post(url, data);
export const put = (url: string, data: any) => axiosInstance.put(url, data);
export const del = (url: string, data?: any) =>
 axiosInstance.delete(url, { data });
export const patch = (url: string, data: any) => axiosInstance.patch(url, data);

export default { get, post, put, del, patch };

type RetryRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };
type QueueItem = {
 resolve: (token: string) => void;
 reject: (error: unknown) => void;
};

let isRefreshing = false;
let failedQueue: QueueItem[] = [];
const REFRESH_BUFFER_SECONDS = 120;
const AUTH_IGNORED_URLS = ['/Identity/v1/login']; // , '/Identity/v1/login/refresh-token' ?

const processQueue = (error: unknown, accessToken: string | null) => {
 failedQueue.forEach((item) => {
  if (error) {
   item.reject(error);
   return;
  }
  item.resolve(accessToken || '');
 });
 failedQueue = [];
};

const isTokenExpiringSoon = (accessToken: string) => {
 try {
  const decoded = jwtDecode<{ exp?: number }>(accessToken);
  if (!decoded?.exp) {
   return true;
  }
  const nowSeconds = Math.floor(Date.now() / 1000);
  return decoded.exp - nowSeconds <= REFRESH_BUFFER_SECONDS;
 } catch {
  return true;
 }
};

const shouldSkipAuthForUrl = (url?: string) => {
 if (!url) {
  return false;
 }
 return AUTH_IGNORED_URLS.includes(url);
};

const refreshAccessToken = async (
 accessToken: string,
 refreshToken: string,
) => {
 if (isRefreshing) {
  return new Promise<string>((resolve, reject) => {
   failedQueue.push({ resolve, reject });
  });
 }

 isRefreshing = true;
 try {
  const refreshResponse = await axios.post<JWTTokenDTO>(
   '/Identity/v1/login/refresh-token',
   {
    access_token: accessToken,
    refresh_token: refreshToken,
   },
   axiosConfig,
  );

  const accessTokenNew = refreshResponse.data.access_token || '';
  if (!accessTokenNew) {
   throw new Error('Missing access token in refresh response');
  }

  persistAuthTokens(refreshResponse.data);
  axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessTokenNew}`;
  processQueue(null, accessTokenNew);
  return accessTokenNew;
 } catch (refreshError) {
  processQueue(refreshError, null);
  throw refreshError;
 } finally {
  isRefreshing = false;
 }
};

// Add a request interceptor
axiosInstance.interceptors.request.use(
 async (
  config: InternalAxiosRequestConfig,
 ): Promise<InternalAxiosRequestConfig> => {
  const { accessToken } = getAuthCookies();
  const { refreshToken } = getAuthCookies();
  //   console.log(config.data instanceof FormData, typeof config.data, config);
  let contentType = 'application/json';
  if (
   config.data instanceof FileList ||
   config.data instanceof FormData ||
   (typeof config.data === 'object' && 'uploads' in (config.data || {}))
  ) {
   contentType = 'multipart/form-data';
  }
  if (config.method === 'patch') {
   contentType = 'application/json-patch+json';
  }
  if (shouldSkipAuthForUrl(config.url)) {
   config.headers.set('Content-Type', contentType);
   return config;
  }

  if (accessToken && accessToken !== 'fail') {
   let tokenToUse = accessToken;
   if (refreshToken && isTokenExpiringSoon(accessToken)) {
    try {
     tokenToUse = await refreshAccessToken(accessToken, refreshToken);
    } catch {
     clearAuthTokens();
     window.location.href = '/login';
    }
   }
   config.headers.set('Content-Type', contentType);
   config.headers.set('Authorization', `Bearer ${tokenToUse}`);
  } else {
   config.headers.set('Content-Type', contentType);
  }
  return config;
 },
);

// Add a response interceptor
axiosInstance.interceptors.response.use(
 (response: AxiosResponse<JWTTokenDTO>) =>
  // Any status code that lie within the range of 2xx cause this function to trigger
  {
   if (
    response.config.url === '/Identity/v1/login' &&
    response.status === 200 &&
    response.data.access_token &&
    response.data.refresh_token &&
    response.data.expiration &&
    !response.data.requires2FA
   ) {
    // test
    // const decoded = jwtDecode(response.data.access_token);
    // cookie set was put in identityLoginSaga
   }
   return response;
  },
 async (error) => {
  const originalRequest = error.config as RetryRequestConfig;
  if (
   error?.response?.status !== 401 ||
   !originalRequest ||
   originalRequest._retry
  ) {
   return Promise.reject(error);
  }

  if (originalRequest.url === '/Identity/v1/login') {
   return Promise.reject(error);
  }

  originalRequest._retry = true;

  try {
   const { accessToken, refreshToken } = getAuthCookies();
   if (!accessToken || !refreshToken) {
    clearAuthTokens();
    window.location.href = '/login';
    return Promise.reject(error);
   }

   const accessTokenNew = await refreshAccessToken(accessToken, refreshToken);
   originalRequest.headers.set('Authorization', `Bearer ${accessTokenNew}`);
   return await axiosInstance(originalRequest);
  } catch (refreshError) {
   clearAuthTokens();
   window.location.href = '/login';
   return Promise.reject(refreshError);
  }
 },
);
